/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from 'react';

import Modal from '../../Modal';
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { formatDateBr } from '../../../utils/formatDate';
import { formatFilaStatus } from '../../../utils/formatFilaStatus';
import api from '../../../services/api';
import { useNavigate } from 'react-router-dom';
import { TipoCarretaValueEnum } from '../../../constants/veiculos';
import { useToast } from '../../../hooks/toast';

interface IModalProps {
  isOpen: boolean;
  setIsOpen: () => void;
  filaData: any[];
}

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: string) => string;
}

const columns: Column[] = [
  {
    id: 'data',
    label: 'Data',
    minWidth: 170,
    align: 'center',
    format: (value: string) => formatDateBr(value),
  },
  { id: 'filaid', label: 'Fila Id', minWidth: 100, align: 'center' },
  { id: 'spot', label: 'Spot', minWidth: 100, align: 'center' },
  {
    id: 'placa',
    label: 'Placa',
    minWidth: 100,
    align: 'center',
  },
  {
    id: 'cpf',
    label: 'CPF',
    minWidth: 140,
    align: 'center',
  },
  {
    id: 'volume',
    label: 'Volume',
    minWidth: 140,
    align: 'center',
  },
  {
    id: 'status',
    label: 'Status',
    minWidth: 170,
    align: 'center',
    format: (value: string) => formatFilaStatus(value),
  },
];

const ModalTodoChecklist = React.forwardRef<HTMLInputElement, IModalProps>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ isOpen, setIsOpen, filaData }, ref) => {
    const navigate = useNavigate();
    const { addToast } = useToast();

    const [loading, setLoading] = useState(false);

    const [loadedFilaData, setLoadedFilaData] = useState<any[]>([]);

    useEffect(() => {
      setLoadedFilaData(filaData);
    }, [filaData]);

    async function handleDoChecklist(fila: any): Promise<void> {
      const agendamento = await api.get(
        `/agendamentos/byagendamentoid/${fila.agendamentoid}`,
      );

      const transportadoraData = await api.get(`/transportadoras/cnpj/`, {
        params: {
          cnpj: agendamento.data.cnpj,
        },
      });

      const data = {
        ...fila,
        pedido: agendamento.data.pedido,
        agendamentoData: agendamento.data,
        transportadoraData: transportadoraData.data,
      };

      const produto = Number(agendamento.data.produto);

      // VHP
      if (produto === 4) {
        navigate('/checklist-vhp', {
          state: {
            params: data,
          },
        });

        return;
      }

      // Bagaco and Fertilizante we approve the checklist directly
      if ([3, 7].includes(produto)) {
        try {
          setLoading(true);

          await api.post('/fila/checklist', {
            data: agendamento.data.data,
            cpf: agendamento.data.cpfmotorista,
            pedido: Number(agendamento.data.pedido),
            volumeordem: Number(agendamento.data.volumeordem),
            tipocarreta: TipoCarretaValueEnum[data.tipocarreta],
            dnit: false,
            lacres: '',
            agendamentoid: agendamento.data.agendamentoid,
            printerName: 'Logistica',
          });

          setLoadedFilaData(currentFila =>
            currentFila.filter(item => item.filaid !== fila.filaid),
          );

          addToast({
            type: 'success',
            title: 'Checklist',
            description: 'Checklist aprovado com sucesso',
            timeout: 1500,
          });
        } catch (error) {
          addToast({
            type: 'error',
            title: 'Erro',
            description: 'Não foi possível aprovar o checklist',
            timeout: 3000,
          });
        } finally {
          setLoading(false);
        }

        return;
      }

      // Etanol
      if ([1, 2, 5, 6, 8, 9, 10].includes(produto)) {
        navigate('/checklist-etanol', {
          state: {
            params: data,
          },
        });

        return;
      }

      // Optional: handle unexpected products
      console.error('Produto sem checklist configurado:', produto);
    }

    return (
      <Modal isOpen={isOpen} setIsOpen={setIsOpen} size="1200px">
        <h1
          style={{
            color: '#00a859',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          Lista Aprovar Checklist
        </h1>

        <h6
          style={{
            textAlign: 'center',
            marginBottom: '20px',
            opacity: 0.7,
          }}
        >
          Clique em um agendamento da tabela abaixo para iniciar o checklist.
        </h6>
        <Paper sx={{ width: '100%', overflow: 'auto' }}>
          <TableContainer sx={{ maxHeight: 640, padding: 2 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: '#00a859' }} />
              </div>
            ) : (
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map(column => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        style={{ minWidth: column.minWidth }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadedFilaData.length > 0 ? (
                    loadedFilaData.map(fila => {
                      return (
                        <TableRow
                          key={fila.filaid}
                          sx={{
                            cursor: 'pointer',
                          }}
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          onClick={() => {
                            handleDoChecklist(fila);
                          }}
                        >
                          {columns.map(column => {
                            const value = fila[column.id];
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {column.format ? column.format(value) : value}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Nenhum agendamento encontrado.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Paper>
      </Modal>
    );
  },
);

ModalTodoChecklist.displayName = 'ModalTodoChecklist';

export default ModalTodoChecklist;
